// @flow

import React, {
  useEffect, useState, useRef,
} from 'react';
import FocusLock from 'react-focus-lock';

import {
  HotspotContainer,
  HotspotContent,
  HotspotCloseButton,
} from './style';
import { TraditionHotspot } from '../TraditionHotspot';
import { CelebrationArtistHotspot } from '../CelebrationArtistHotspot';
import { CelebrationHotspot } from '../CelebrationHotspot';
import { findArtistData } from '../../constants/artistDataMap';

type Props = {
  activeArtist: string,
  onClose: Function,
  activeFilter: 'traditions' | 'celebrations' | 'none',
  setActiveFilter: Function,
  selectedCelebration: Object,
  handleNextCelebration: Function,
  handlePrevCelebration: Function,
  setActiveArtist: Function,
};

export const HotSpot = ({
  activeArtist,
  onClose,
  activeFilter,
  setActiveFilter,
  selectedCelebration,
  handleNextCelebration,
  handlePrevCelebration,
  setActiveArtist,
}: Props) => {
  const [activeData, setActiveData] = useState();
  const closeRef = useRef(null);
  const hotSpotRef = useRef(null);
  const hotSpotContentRef = useRef(null);
  const isMobile = window.innerWidth < 720;


  useEffect(() => {
    if (activeArtist && selectedCelebration && activeFilter === 'celebrations') {
      setActiveData(findArtistData(activeArtist));
    } else if (activeArtist && activeFilter !== 'celebrations') {
      setActiveData(findArtistData(activeArtist));
    } else if (selectedCelebration && activeFilter === 'celebrations') {
      setActiveData(selectedCelebration);
    } else {
      setActiveData(null);
    }

    window.scrollTo(0, 0);
    try {
      if (hotSpotContentRef.current) {
        hotSpotContentRef.current.scrollTo(0, 0);
      }
    } catch (err) {
      // handle errors for older browsers or when ref is not available
    }
  }, [activeArtist, selectedCelebration, activeFilter]);

  // Move focus into the panel when it opens so screen readers can access content
  useEffect(() => {
    if (!activeData) return;
    const afterTransition = setTimeout(() => {
      if (closeRef.current) {
        closeRef.current.focus();
      }
    }, 650);
    return () => clearTimeout(afterTransition);
  }, [activeData]);

  const handleClose = () => {
    if (activeFilter === 'celebrations' && selectedCelebration) {
      setActiveFilter('celebrations');
      onClose();
    } else if (activeFilter === 'traditions' && selectedCelebration) {
      setActiveFilter('celebrations');
      onClose();
    } else {
      onClose();
    }
  };

  const dialogLabel = activeData
    ? (activeData.name
        ? `${activeData.name} — artist information`
        : activeData.celebrationTitle
          ? `${activeData.celebrationTitle} — celebration`
          : 'Hotspot')
    : 'Hotspot';

  return (
    <HotspotContainer
      role='dialog'
      aria-modal='true'
      aria-label={dialogLabel}
      id='hotspot'
      ref={hotSpotRef}
      className={isMobile && activeFilter === 'celebrations' ? 'celebrations' : ''}
    >
      <FocusLock returnFocus={false}>
        <HotspotCloseButton onClick={handleClose} ref={closeRef} aria-label='Close Hotspot'>
            Close Hotspot
        </HotspotCloseButton>
        <HotspotContent
          id='hotspot-panel-content'
          ref={hotSpotContentRef}
          tabIndex={-1}
        >
          {activeData && activeFilter === 'celebrations' && activeArtist && (
            <CelebrationArtistHotspot
              activeData={activeData}
              selectedCelebration={selectedCelebration.celebrationTitle}
              handleExploreClick={() => setActiveFilter('traditions')}
            />
          )}
          {activeData && (activeFilter === 'traditions' || activeFilter === 'none') && (
            <TraditionHotspot activeData={activeData} />
          )}
          {selectedCelebration && activeFilter === 'celebrations' && activeData !== findArtistData(activeArtist) && (
            <CelebrationHotspot
              selectedCelebration={selectedCelebration}
              handleNextCelebration={handleNextCelebration}
              handlePrevCelebration={handlePrevCelebration}
              setActiveArtist={setActiveArtist}
            />
          )}
        </HotspotContent>
      </FocusLock>
    </HotspotContainer>
  );
};
